import React from 'react';
import {
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  MegaphoneIcon,
  CalendarIcon,
  ChartBarIcon,
  PauseIcon,
  PlayIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

const STATUSES = {
  DRAFT: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  SCHEDULED: { label: 'Scheduled', color: 'bg-blue-100 text-blue-800' },
  ACTIVE: { label: 'Active', color: 'bg-green-100 text-green-800' },
  PAUSED: { label: 'Paused', color: 'bg-yellow-100 text-yellow-800' },
  COMPLETED: { label: 'Completed', color: 'bg-purple-100 text-purple-800' },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-800' },
};

const CampaignList = ({
  campaigns,
  audiences,
  searchTerm,
  setSearchTerm,
  onEdit,
  onDelete,
  onViewAnalytics,
  onPause,
  onResume,
  onSendNow,
  page,
  setPage,
}) => {
  const filteredCampaigns = campaigns.filter((campaign) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      campaign.name?.toLowerCase().includes(searchLower) ||
      campaign.description?.toLowerCase().includes(searchLower)
    );
  });

  const getAudienceName = (audienceId) => {
    const audience = audiences.find((a) => a.id === audienceId);
    return audience?.name || 'Unknown Audience';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString).toLocaleString();
  };

  const canDelete = (status) => {
    return status === 'DRAFT' || status === 'CANCELLED' || status === 'COMPLETED';
  };

  const canSendNow = (status) => {
    return status === 'DRAFT' || status === 'SCHEDULED';
  };

  return (
    <div>
      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Grid */}
      {filteredCampaigns.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <MegaphoneIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create a campaign to start reaching your audience.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredCampaigns.map((campaign) => {
            const status = STATUSES[campaign.status] || STATUSES.DRAFT;
            const content = campaign.content || {};

            return (
              <div
                key={campaign.id}
                className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {campaign.name}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                        {campaign.description || 'No description'}
                      </p>
                    </div>
                  </div>

                  {/* Campaign details */}
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <span className="font-medium mr-2">Audience:</span>
                      {getAudienceName(campaign.audienceId)}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <span className="font-medium mr-2">Channel:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {campaign.channel}
                      </span>
                    </div>
                    {content.subject && (
                      <div className="flex items-center text-sm text-gray-500">
                        <span className="font-medium mr-2">Subject:</span>
                        <span className="truncate">{content.subject}</span>
                      </div>
                    )}
                    <div className="flex items-center text-sm text-gray-500">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {formatDate(campaign.startTime)}
                    </div>
                  </div>

                  {/* Performance (if available) */}
                  {campaign.performance && Object.keys(campaign.performance).length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs font-medium text-gray-500 mb-2">Performance</p>
                      <div className="flex gap-3 flex-wrap">
                        {Object.entries(campaign.performance).map(([key, value]) => (
                          <span key={key} className="text-xs text-gray-600">
                            <span className="font-medium">{key}:</span> {Array.isArray(value) ? value.length : value}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-between pt-4 border-t gap-2">
                    <div className="flex gap-2">
                      {/* Analytics button - show for non-draft campaigns */}
                      {campaign.status !== 'DRAFT' && (
                        <button
                          onClick={() => onViewAnalytics && onViewAnalytics(campaign)}
                          className="text-purple-600 hover:text-purple-800 flex items-center gap-1 text-sm"
                          title="View Analytics"
                        >
                          <ChartBarIcon className="h-5 w-5" />
                          <span className="hidden sm:inline">Analytics</span>
                        </button>
                      )}

                      {/* Send Now button for DRAFT or SCHEDULED campaigns */}
                      {canSendNow(campaign.status) && onSendNow && (
                        <button
                          onClick={() => onSendNow(campaign.id)}
                          className="text-green-600 hover:text-green-800 flex items-center gap-1 text-sm font-medium"
                          title="Send Now"
                        >
                          <PaperAirplaneIcon className="h-5 w-5" />
                          <span className="hidden sm:inline">Send Now</span>
                        </button>
                      )}

                      {/* Pause/Resume buttons for active campaigns */}
                      {campaign.status === 'ACTIVE' && onPause && (
                        <button
                          onClick={() => onPause(campaign.id)}
                          className="text-yellow-600 hover:text-yellow-800 flex items-center gap-1 text-sm"
                          title="Pause Campaign"
                        >
                          <PauseIcon className="h-5 w-5" />
                          <span className="hidden sm:inline">Pause</span>
                        </button>
                      )}

                      {campaign.status === 'PAUSED' && onResume && (
                        <button
                          onClick={() => onResume(campaign.id)}
                          className="text-green-600 hover:text-green-800 flex items-center gap-1 text-sm"
                          title="Resume Campaign"
                        >
                          <PlayIcon className="h-5 w-5" />
                          <span className="hidden sm:inline">Resume</span>
                        </button>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => onEdit(campaign)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => onDelete(campaign.id)}
                        disabled={!canDelete(campaign.status)}
                        className={`${
                          canDelete(campaign.status)
                            ? 'text-red-400 hover:text-red-600'
                            : 'text-gray-300 cursor-not-allowed'
                        }`}
                        title={!canDelete(campaign.status) ? 'Only DRAFT or CANCELLED campaigns can be deleted' : ''}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {filteredCampaigns.length > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{filteredCampaigns.length}</span> campaigns
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignList;

